import {
  Text,
  useToast,
  Divider,
  Flex,
  Box,
  Button,
  useDisclosure,
  VStack,
  Checkbox,
  Spacer,
  ListItem,
  UnorderedList,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';
import { commonFormulas } from '@finley-technologies/formula-engine';

import { DeleteConfirmationModal } from '../../../delete/DeleteArrayPropertyButton';
import { PreviewPostgresQueryButton } from '../PreviewPostgresQueryButton';
import { PostgresQueryDateFormulaField } from './LenderPostgresQueryComponents';
import { QueryInputTypeBadge } from './QueryInputTypeBadge';
import { colors } from 'packages/common-frontend/colors';
import {
  CopyIconButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { AddQueryInputModal } from 'packages/settings/ui/AddQueryInputModal';

type TAccountDetails = { counterpartySlug: string; tAccount: string };
type GroupedTAccountDetails = { [counterparty: string]: TAccountDetails[] };

const groupTAccountsDetailsByCounterparty = (
  tAccountsDetails: TAccountDetails[],
): GroupedTAccountDetails => {
  return _.groupBy(
    tAccountsDetails,
    ({ counterpartySlug }) => counterpartySlug,
  );
};

type TAccountsEditProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  tAccountsSumQuery: CreditAgreement.Config.CommonPostgresQuery.Query.TAccountsSumQuery;
};
const TAccountsField = ({
  wipConfig,
  fullPropertyKey,
  onSave,
  isSaveLoading,
  tAccountsSumQuery,
}: TAccountsEditProps) => {
  const toast = useToast();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const initialTAccounts =
    tAccountsSumQuery.parameterConfig.tAccountDetails.accountDetails;
  const initialGroupedTAccounts = groupTAccountsDetailsByCounterparty(
    initialTAccounts,
  );

  const [latestTAccounts, setLatestTAccounts] = useState<TAccountDetails[]>(
    initialTAccounts,
  );

  const allGroupedTAccounts: GroupedTAccountDetails | null = useMemo(() => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return null;
    }

    const creditFacilityId = getIn(
      wipConfig.formProps.values,
      'creditFacilityId',
    );

    const mappedTAccounts = _.compact(
      _.map(wipConfig.tAccounts, (tAccount) => {
        if (tAccount.creditFacilityId !== creditFacilityId) {
          return null;
        }

        const org = wipConfig.organizationMap[tAccount.organizationId];
        if (!org) {
          toast({
            position: 'top',
            status: 'error',
            isClosable: true,
            duration: 5000,
            title: `Organization does not exist for tAccount ${tAccount.tAccount} orgId ${tAccount.organizationId}. This needs to be resolved to continue.`,
          });
          return null;
        }

        return {
          tAccount: tAccount.tAccount,
          counterpartySlug: org.entity.slug,
        };
      }),
    );

    return _.groupBy(
      mappedTAccounts,
      ({ counterpartySlug }) => counterpartySlug,
    );
  }, [fullPropertyKey, wipConfig, toast]);

  const getIsChecked: (tAccount: TAccountDetails) => boolean = useCallback(
    (tAccount: TAccountDetails) => {
      return !!_.find(
        latestTAccounts,
        (latestTAccount) =>
          latestTAccount.counterpartySlug === tAccount.counterpartySlug &&
          latestTAccount.tAccount === tAccount.tAccount,
      );
    },
    [latestTAccounts],
  );

  const onToggleTAccount = useCallback(
    ({
      tAccountToToggle,
      newIsCheckedState,
    }: {
      tAccountToToggle: TAccountDetails;
      newIsCheckedState: boolean;
    }) => {
      if (newIsCheckedState === true) {
        // check whether toggled t-account already exists in latest state
        const tAccountsCopy = _.cloneDeep(latestTAccounts);
        const existingTAccountInLatestState = _.find(
          tAccountsCopy,
          (tAccountExisting) =>
            tAccountExisting.counterpartySlug ===
              tAccountToToggle.counterpartySlug &&
            tAccountExisting.tAccount === tAccountToToggle.tAccount,
        );

        // add t-account to the latest state
        if (!existingTAccountInLatestState) {
          tAccountsCopy.push(tAccountToToggle);
        }
        setLatestTAccounts(tAccountsCopy);
      } else {
        // remove the t-account from the latest state
        const newTAccounts = _.filter(
          latestTAccounts,
          (latestTAccount) => !_.isEqual(latestTAccount, tAccountToToggle),
        );
        setLatestTAccounts(newTAccounts);
      }
    },
    [latestTAccounts],
  );

  return (
    <Box
      p="32px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      {isEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
        <>
          <Box>
            {_.map(
              allGroupedTAccounts,
              (tAccountCounterpartyGroup, counterparty) => (
                <Box key={`edit-checkboxes-${counterparty}`} mb="24px">
                  <Text textAlign="left" fontWeight={500} mb="16px">
                    {commonUtils.toTitleCase(counterparty)}
                  </Text>

                  <VStack
                    alignItems="flex-start"
                    alignSelf="center"
                    spacing="16px"
                  >
                    {_.map(tAccountCounterpartyGroup, (tAccountDetails) => (
                      <Checkbox
                        key={`checkbox-${tAccountDetails.counterpartySlug}-${tAccountDetails.tAccount}`}
                        onChange={(event) => {
                          onToggleTAccount({
                            tAccountToToggle: tAccountDetails,
                            newIsCheckedState: event.target.checked,
                          });
                        }}
                        spacing="16px"
                        isChecked={getIsChecked(tAccountDetails)}
                      >
                        <Text color={colors.black[70]} textAlign="left">
                          {commonUtils.toTitleCase(tAccountDetails.tAccount)}
                        </Text>
                      </Checkbox>
                    ))}
                  </VStack>
                </Box>
              ),
            )}
          </Box>
          <Divider />
          <Flex alignItems="center" mt="24px">
            {!latestTAccounts.length && (
              <Text color={colors.black[70]}>
                Must have at least one T-account selected.
              </Text>
            )}
            <Spacer />
            <>
              <Button
                color={colors.aurora[400]}
                border="2px"
                borderRadius="8px"
                borderColor={colors.aurora[400]}
                backgroundColor="white"
                onClick={() => {
                  setLatestTAccounts(initialTAccounts);
                  onEditClose();
                }}
                ml="32px"
                mr="24px"
              >
                Cancel
              </Button>
              <Button
                color="white"
                backgroundColor={colors.aurora[400]}
                isLoading={isSaveLoading}
                onClick={async () => {
                  try {
                    const updatedQuery = _.cloneDeep(tAccountsSumQuery);
                    updatedQuery.parameterConfig.tAccountDetails.accountDetails = latestTAccounts;

                    await onSave(updatedQuery);
                  } catch (err) {
                    toast({
                      ...errorToastConfig,
                      id: 'saveError',
                      title: 'Error saving T-Accounts. Please try again.',
                    });
                  } finally {
                    onEditClose();
                  }
                }}
                isDisabled={!latestTAccounts.length}
              >
                Save
              </Button>
            </>
          </Flex>
        </>
      ) : (
        <Flex
          alignItems={_.size(initialGroupedTAccounts) ? 'top' : 'center'}
          justifyContent="space-between"
        >
          {_.size(initialGroupedTAccounts) ? (
            <VStack alignItems="flex-start" alignSelf="center" spacing="24px">
              {_.map(
                initialGroupedTAccounts,
                (tAccountCounterpartyGroup, counterparty) => (
                  <Box key={`edit-checkboxes-${counterparty}`}>
                    <Text textAlign="left" fontWeight={500} mb="16px">
                      {commonUtils.toTitleCase(counterparty)}
                    </Text>

                    <UnorderedList>
                      {_.map(tAccountCounterpartyGroup, (tAccountDetails) => (
                        <ListItem
                          key={`list-item-${counterparty}-${tAccountDetails.tAccount}`}
                          color={colors.black[70]}
                          textAlign="left"
                        >
                          {commonUtils.toTitleCase(tAccountDetails.tAccount)}
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                ),
              )}
            </VStack>
          ) : (
            <Text color={colors.black[70]}>
              Please select at least one T-Account
            </Text>
          )}

          <EditIconButton
            label="Edit T-Accounts"
            ariaLabel="edit t accounts"
            onClick={onEditOpen}
          />
        </Flex>
      )}
    </Box>
  );
};

type LenderTAccountsSumViewProps = {
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.TAccountsSumQuery;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  duplicateParams: {
    isDuplicateQueryOpen: boolean;
    onDuplicateQueryOpen: () => void;
    onDuplicateQueryClose: () => void;
    duplicateQuery: (params: { queryKeyInput: string }) => Promise<void>;
    isDuplicateLoading: boolean;
  };
  deleteParams: {
    isDeleteConfirmationModalOpen: boolean;
    onDeleteConfirmationModalOpen: () => void;
    onDeleteConfirmationModalClose: () => void;
    deleteQuery: () => Promise<void>;
    isDeleteLoading: boolean;
  };
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  triggerDate?: Util.FullDateStr;
};
export const LenderTAccountsSumView = ({
  postgresQuery,
  wipConfig,
  fullPropertyKey,
  duplicateParams,
  deleteParams,
  onSave,
  isSaveLoading,
  presetVariables,
  inputDateType,
  triggerDate,
}: LenderTAccountsSumViewProps) => {
  const {
    isDuplicateQueryOpen,
    onDuplicateQueryOpen,
    onDuplicateQueryClose,
    duplicateQuery,
    isDuplicateLoading,
  } = duplicateParams;
  const {
    isDeleteConfirmationModalOpen,
    onDeleteConfirmationModalOpen,
    onDeleteConfirmationModalClose,
    deleteQuery,
    isDeleteLoading,
  } = deleteParams;

  const evaluatedDates = useMemo(() => {
    if (!triggerDate) {
      return null;
    }
    return commonFormulas.evaluateStartAndEndDateFormulas({
      startFormula: postgresQuery.parameterConfig.startDateFormula.formula,
      endFormula: postgresQuery.parameterConfig.endDateFormula.formula,
      variables: [
        { name: 'triggerDate', value: triggerDate, parseNumber: true },
        ...postgresQuery.variables,
      ],
    });
  }, [postgresQuery, triggerDate]);

  return (
    <Box>
      <Flex alignItems="center" mb="24px" justifyContent="right">
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="tAccountsSum"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>
      <Divider />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="64px"
        mb="24px"
      >
        <Text fontWeight={600} fontSize="20px">
          Key
        </Text>
        <QueryInputTypeBadge type="tAccountsSum" />
      </Flex>
      <Text
        color={colors.black[70]}
        p="32px"
        borderWidth="1px"
        borderRadius="8px"
        borderColor={colors.gray[200]}
      >
        {postgresQuery.name}
      </Text>

      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Starting Date
      </Text>
      <PostgresQueryDateFormulaField
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        postgresQuery={postgresQuery}
        formulaFieldKey="startDateFormula"
        evaluatedDate={evaluatedDates?.startDate}
        triggerDate={triggerDate}
      />
      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Ending Date
      </Text>
      <PostgresQueryDateFormulaField
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        postgresQuery={postgresQuery}
        formulaFieldKey="endDateFormula"
        evaluatedDate={evaluatedDates?.endDate}
        triggerDate={triggerDate}
      />

      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        T-Accounts
      </Text>
      <TAccountsField
        fullPropertyKey={fullPropertyKey}
        wipConfig={wipConfig}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        tAccountsSumQuery={postgresQuery}
      />
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <PreviewPostgresQueryButton
          wipConfig={wipConfig}
          postgresQuery={postgresQuery}
          inputDateType={inputDateType}
          presetVariables={presetVariables}
          queryType={postgresQuery.type}
          startDate={evaluatedDates?.startDate}
          endDate={evaluatedDates?.endDate}
          selectedInputDate={triggerDate}
        />
      )}
    </Box>
  );
};
